import { hasValue, MultiValidatorFn } from "@repo/helpers";
import { useCallback, useEffect, useState } from "react";

import { ComponentValidationResult } from "../models/mappedComponent";
import { MappedComponent, MappedSection } from "./types";
import { getValidator } from "./validation";
import ValidationResults from "./validationResults";

export function useMappedSections(
	getSections: () => MappedSection[],
	dependencies: any[],
	customValidator?: (component: MappedComponent) => MultiValidatorFn<any, ComponentValidationResult>,
) {
	const [mappedSections, setMappedSections] = useState(getSections);
	const [validationResults, setValidationResults] = useState(() => new ValidationResults([]));
	const [completed, setCompleted] = useState(false);

	useEffect(() => {
		setValidationResults(validate());
	}, []);

	useEffect(() => {
		updateMappedSections();
	}, dependencies);

	useEffect(() => {
		const newValidationResults = validate();

		const changes = newValidationResults.filter((v) => validationResults.getValue(v.id) !== v.value);

		//If no validation result has changed, and we have the same number of validation results (in case, say, added account)
		if (!changes.any() && newValidationResults.count() === validationResults.count()) return;
		setValidationResults(newValidationResults);
	}, [mappedSections, ...dependencies]);

	useEffect(() => {
		const errors = validationResults.filter((r) => hasValue(r.value));
		setCompleted(!errors.any());
	}, [validationResults]);

	const updateMappedSections = useCallback(() => {
		setMappedSections(getSections());
	}, [getSections]);

	function validate() {
		const validationResultsArr = mappedSections.map(validateSection).flat(4);

		return new ValidationResults(validationResultsArr);
	}

	function validateSection(section: MappedSection) {
		if (!section.visible) return [] as ComponentValidationResult[];

		//Get validation results for components under subsections
		const subSectionValidation = section.childSections.map(validateSection) as ComponentValidationResult[][];

		const subSectionSummary = subSectionValidation
			.filter(
				(v) =>
					v
						.flat(4)
						.map((r) => r.value)
						.filter((o) => o).length,
			)
			.map((r) => r[r.length - 1])
			.map((r) => {
				return {
					component: r.component,
					value: `${(r.component as MappedSection).label} is incomplete`,
				};
			});

		const hasPropName = (c: MappedComponent) => hasValue(c.propertyName);
		const sectionValidation = section.components
			.filter(hasPropName)
			.map(getValidateComponent)
			.flat(4) as ComponentValidationResult[];

		//manual entry for the section (used in the list)
		const summaryValidation = {
			component: {
				id: section.section.id,
				propertyName: section.propertyName,
				label: section.label,
			},
			value:
				sectionValidation
					.concat(subSectionSummary)
					.map((r) => r.value)
					.filter((o) => o)
					.join(", ") || null,
		} as ComponentValidationResult;

		return sectionValidation.concat(subSectionValidation.flat(4)).concat(summaryValidation);
	}

	function getValidateComponent(component: MappedComponent) {
		const validatorFn = customValidator ?? getValidator;
		const validationResult = validatorFn(component)(component.propertyValue);
		return traverseValidationResult(component, validationResult);
	}

	function traverseValidationResult(
		component: MappedComponent,
		validationResult: string | ComponentValidationResult[],
	) {
		if (Array.isArray(validationResult)) {
			if (validationResult.map((vr) => vr.value).some((r) => Array.isArray(r))) {
				const filteredValidationResult = validationResult.filter((r) => !Array.isArray(r.value));
				const innerValidations = validationResult
					.filter((r) => Array.isArray(r.value))
					.map((r) => r.value)
					.flat() as ComponentValidationResult[];
				return traverseValidationResult(component, [...filteredValidationResult, ...innerValidations]);
			}

			const anyErrors = validationResult.map((r) => r.value).filter((o) => o).length;
			const summaryResult = {
				component: component,
				value: anyErrors && component.label ? `${component.label} is incomplete` : null,
			} as ComponentValidationResult;

			return [...validationResult, summaryResult];
		}
		return {
			component: component,
			value: validationResult,
		} as ComponentValidationResult;
	}

	return { mappedSections, validationResults, completed, updateMappedSections };
}
